import { defineStore } from 'pinia';
import { union, type UnionFunctionType } from '@/helper/lodashHelper';
import type {
  OverviewState,
  OverviewAction,
  Overview,
  OverviewActionPayload,
  OverviewActionLoading,
  OverviewActionFilter,
  FilterOptions,
  Payload,
  OverviewActionError,
} from './types';

// We need unique itemIds therefore we create a Prefix-ID from Context Type, Type and Subtype
const createNumberPrefix = function (string: string) {
  let hash = 0;
  for (const char of string) hash += char.codePointAt(0)!;
  return hash;
};

export const getEndpoint = function (
  context: string,
  type: string,
  subType?: string,
) {
  return [context, type, subType].filter((item) => !!item).join('.') || '';
};

const extendPayload = function (
  payload: Payload,
  type: string,
  idPrefix: number,
  subType?: string,
) {
  if (!payload || payload.length === 0) return [];

  return payload.map((item, index) => {
    let additionalInformation: {
      HAS_CONTEXTLINKS?: boolean;
      FILE_ICON?: string;
      dragType?: string;
      IS_FOLDER?: boolean;
    } = { HAS_CONTEXTLINKS: true };
    const itemId = item.itemId ?? idPrefix + index;

    if (type === 'document') {
      const { DOCFILE = '', TYPE, IS_FOLDER = false } = item;

      const fileType =
        (typeof DOCFILE === 'string' && DOCFILE.split('.').pop()) ?? '';
      const FILE_ICON = IS_FOLDER ? 'folder' : fileType || '';
      const dragType = IS_FOLDER ? 'item' : 'main';
      const HAS_CONTEXTLINKS = !!(subType === 'sharepoint'
        ? IS_FOLDER || TYPE === 'folder'
        : !IS_FOLDER);

      additionalInformation = {
        FILE_ICON,
        dragType,
        IS_FOLDER: !!IS_FOLDER,
        HAS_CONTEXTLINKS,
      };
    }

    return { ...item, itemId, ...additionalInformation };
  });
};

function convertToOverviewObject(
  type: string,
  overview: Overview | Payload,
  idPrefix = 0,
  subType?: string,
) {
  const isArray = Array.isArray(overview);

  const overviewObject = {
    total: isArray
      ? overview.length
      : (overview.total ?? overview.value?.length ?? -1),
    value: isArray ? overview : (overview.value ?? []),
  };

  overviewObject.value = extendPayload(
    overviewObject.value,
    type,
    idPrefix,
    subType,
  );

  return overviewObject;
}

function filterPayload(payload: Payload, filter: FilterOptions) {
  return payload.filter((item) =>
    Object.keys(item).some((key) => {
      const value = item[`${key}_DESCR`] ?? item[key];
      const { search, parentId } = filter;

      const hasSearch =
        search && search.length > 0
          ? value?.toString().toLowerCase().includes(search.toLowerCase())
          : true;

      const hasParentId =
        parentId === undefined ? true : item.PARENTID === parentId;

      return hasSearch && hasParentId;
    }),
  );
}

export const useOverviewStore = defineStore('overview', {
  state: (): OverviewState => ({
    overview: {},
    overviewEdit: {},
  }),
  getters: {
    getPayload:
      (state) => (context: string) => (type: string) => (subType: string) => {
        const overviewObject = state.overview[
          getEndpoint(context, type, subType)
        ] ?? { value: [], filter: { search: '', parentId: '' } };

        const { value, filter } = overviewObject;
        return filter ? filterPayload(value, filter) : value;
      },
    getActiveLink:
      (state) =>
      (context: string) =>
      (linkType: string) =>
      (subType: string) => {
        const payload =
          state.overview[getEndpoint(context, 'linking', subType)];
        return payload?.value.filter(
          (item) => item.URLTYPE === linkType && item.HAUPT_URL === true,
        );
      },
    getFilter:
      (state) => (context: string) => (type: string) => (subType: string) =>
        state.overview[getEndpoint(context, type, subType)]?.filter ?? {},
    getLoading:
      (state) => (context: string) => (type: string) => (subType: string) =>
        state.overview[getEndpoint(context, type, subType)]?.loading ?? false,
    getError:
      (state) => (context: string) => (type: string) => (subType: string) =>
        state.overview[getEndpoint(context, type, subType)]?.error ?? false,
    getEditPayload:
      (state) => (context: string) => (type: string) => (subType: string) =>
        state.overviewEdit[getEndpoint(context, type, subType)] ?? [],
    getPayloadCount:
      (state) => (context: string) => (type: string) => (subType: string) =>
        state.overview[getEndpoint(context, type, subType)]?.total ?? -1, // if the store has no endpoint, it has no count either
    getTypesByContext: (state) => (context: string) =>
      Object.keys(state.overview)
        .filter((key) => key.search(`${context}.`) === 0)
        .map((keyString) => {
          const [contextString, type, subType] = keyString.split('.');
          return { context: contextString, type, subType };
        }) || [],
    getItem:
      (state) =>
      (context: string) =>
      (type: string) =>
      (subType: string) =>
      (itemId: number | string) => {
        const payload =
          state.overview[getEndpoint(context, type, subType)]?.value ?? [];
        return payload.find((item) => itemId === item.itemId) ?? {};
      },
  },
  actions: {
    setOverview(overviewConfig: OverviewActionPayload) {
      const { context, type, subType = '', payload = [] } = overviewConfig;
      const endpoint = getEndpoint(context, type, subType);
      const idPrefix = Number.parseInt(
        `${createNumberPrefix(endpoint)}00000`,
        10,
      );

      const overviewObject =
        type === 'all'
          ? { total: payload.length, value: payload }
          : convertToOverviewObject(type, payload, idPrefix, subType);

      this.overview[endpoint] = {
        ...this.overview[endpoint],
        ...overviewObject,
      };
    },
    extendOverview(
      overviewConfig: OverviewActionPayload,
      unionFunction: UnionFunctionType,
    ) {
      const { context, type, subType, payload = [] } = overviewConfig;
      const endpoint = getEndpoint(context, type, subType);
      const overview = this.overview[endpoint];
      if (overview) {
        const newOverviewValue = union(
          overview.value,
          extendPayload(payload, type, 0, subType),
          unionFunction,
        );
        this.overview[endpoint] = {
          value: newOverviewValue,
          total: payload.length,
          filter: overview.filter,
        };
      } else {
        this.overview[endpoint] = {
          value: payload,
          total: payload.length,
        };
      }
    },
    resetOverviewByContext(context: string) {
      Object.keys(this.overview).forEach((endpoint) => {
        if (endpoint.split('.')[0] === context) {
          delete this.overview[endpoint];
        }
      });
    },
    setEditPayload(overviewConfig: OverviewActionPayload) {
      const { context, type, subType, payload = [] } = overviewConfig;
      this.overviewEdit[getEndpoint(context, type, subType)] = payload;
    },
    resetEditPayload(overviewConfig: OverviewAction) {
      const { context, type, subType } = overviewConfig;
      delete this.overviewEdit[getEndpoint(context, type, subType)];
    },
    setFilter(overviewConfig: OverviewActionFilter, updateTotal = false) {
      const { context, type, subType, filter } = overviewConfig;
      const target = this.overview[getEndpoint(context, type, subType)];
      if (target) {
        target.filter = filter;
        target.total = updateTotal
          ? filterPayload(target?.value || [], filter).length
          : target.total;
      }
    },
    setCount(overviewConfig: OverviewActionFilter, total = 0) {
      const { context, type, subType } = overviewConfig;
      const target = this.overview[getEndpoint(context, type, subType)];
      if (target) target.total = total;
    },
    updateFilter(overviewConfig: OverviewActionFilter) {
      const { context, type, subType, filter } = overviewConfig;
      const target = this.overview[getEndpoint(context, type, subType)];
      if (target) target.filter = { ...target.filter, ...filter };
    },
    setLoading(overviewConfig: OverviewActionLoading) {
      const { context, type, subType, loading } = overviewConfig;
      const target = this.overview[getEndpoint(context, type, subType)];
      if (target) target.loading = loading;
    },
    setError(overviewConfig: OverviewActionError) {
      const { context, type, subType, error } = overviewConfig;
      const target = this.overview[getEndpoint(context, type, subType)];
      if (target) target.error = error;
    },
  },
});
